// Mock data for Interactive Booth Map (Issue #676)

export interface BoothLayout {
  id: number;
  fair_id: number;
  slot_id: number;
  x_coord: number;
  y_coord: number;
  width: number;
  height: number;
  shape: 'rectangle' | 'square' | 'corner' | 'L-shaped';
  rotation: number;
}

export interface BoothAmenity {
  id: number;
  fair_id: number;
  name: string;
  type: 'restroom' | 'entrance' | 'food_court' | 'parking' | 'stage' | 'seating' | 'water' | 'waste';
  x_coord: number;
  y_coord: number;
  icon: string;
}

// Issue #1078: Align with shared BoothMapSlot type (base_price: string)
// Issue #1159: Added vendor fields for customer booth details modal
export interface SlotWithFeatures {
  id: number;
  fair_id: number;
  slot_number: string;
  dimensions?: string; // Optional to match Slot interface
  has_power: boolean;
  slot_type: string;
  base_price: string; // Issue #890: String for Decimal precision
  is_available: boolean;
  is_corner_booth: boolean; // Required field from Slot interface (Issue #892)
  corner_booth: boolean;
  high_traffic_area: boolean;
  near_entrance: boolean;
  estimated_foot_traffic?: number; // Renamed from historical_foot_traffic to match Slot interface
  // Vendor information for reserved booths (Issue #1159)
  vendor_id?: number;
  vendor_name?: string;
  vendor_business_name?: string;
  vendor_product_types?: string[];
}

export interface BoothNeighbor {
  slot_id: number;
  slot_number: string;
  vendor_id?: number;
  vendor_name?: string;
  distance?: number;
  direction?: string;
}

export const mockBoothLayouts: BoothLayout[] = [
  { id: 1, fair_id: 1, slot_id: 1, x_coord: 50, y_coord: 50, width: 100, height: 100, shape: 'rectangle', rotation: 0 },
  { id: 2, fair_id: 1, slot_id: 2, x_coord: 160, y_coord: 50, width: 100, height: 100, shape: 'rectangle', rotation: 0 },
  { id: 3, fair_id: 1, slot_id: 3, x_coord: 270, y_coord: 50, width: 100, height: 100, shape: 'corner', rotation: 0 },
  { id: 4, fair_id: 1, slot_id: 4, x_coord: 50, y_coord: 160, width: 100, height: 100, shape: 'rectangle', rotation: 0 },
  { id: 5, fair_id: 1, slot_id: 5, x_coord: 160, y_coord: 160, width: 100, height: 100, shape: 'square', rotation: 0 },
  { id: 6, fair_id: 1, slot_id: 6, x_coord: 270, y_coord: 160, width: 100, height: 100, shape: 'rectangle', rotation: 0 },
];

export const mockBoothAmenities: BoothAmenity[] = [
  { id: 1, fair_id: 1, name: 'Main Entrance', type: 'entrance', x_coord: 200, y_coord: 10, icon: '🚪' },
  { id: 2, fair_id: 1, name: 'Restrooms', type: 'restroom', x_coord: 400, y_coord: 100, icon: '🚻' },
  { id: 3, fair_id: 1, name: 'Food Court', type: 'food_court', x_coord: 400, y_coord: 200, icon: '🍴' },
  { id: 4, fair_id: 1, name: 'Parking Lot', type: 'parking', x_coord: 50, y_coord: 300, icon: '🅿️' },
  { id: 5, fair_id: 1, name: 'Main Stage', type: 'stage', x_coord: 200, y_coord: 300, icon: '🎭' },
  { id: 6, fair_id: 1, name: 'Water Station', type: 'water', x_coord: 350, y_coord: 150, icon: '💧' },
];

export const mockSlotsWithFeatures: SlotWithFeatures[] = [
  {
    id: 1,
    fair_id: 1,
    slot_number: 'A1',
    dimensions: '10x10 feet',
    has_power: true,
    slot_type: 'premium',
    base_price: '150.00', // Issue #1078: String for type alignment
    is_available: true,
    is_corner_booth: false, // Required field from Slot interface
    corner_booth: false,
    high_traffic_area: true,
    near_entrance: true,
    estimated_foot_traffic: 500,
  },
  {
    id: 2,
    fair_id: 1,
    slot_number: 'A2',
    dimensions: '10x10 feet',
    has_power: true,
    slot_type: 'common',
    base_price: '100.00', // Issue #1078: String for type alignment
    is_available: true,
    is_corner_booth: false, // Required field from Slot interface
    corner_booth: false,
    high_traffic_area: true,
    near_entrance: false,
    estimated_foot_traffic: 400,
  },
  {
    id: 3,
    fair_id: 1,
    slot_number: 'A3',
    dimensions: '12x12 feet',
    has_power: false,
    slot_type: 'premium',
    base_price: '200.00', // Issue #1078: String for type alignment
    is_available: false,
    is_corner_booth: true, // Required field from Slot interface
    corner_booth: true,
    high_traffic_area: true,
    near_entrance: false,
    estimated_foot_traffic: 600,
  },
  {
    id: 4,
    fair_id: 1,
    slot_number: 'B1',
    dimensions: '10x10 feet',
    has_power: false,
    slot_type: 'common',
    base_price: '80.00', // Issue #1078: String for type alignment
    is_available: true,
    is_corner_booth: false, // Required field from Slot interface
    corner_booth: false,
    high_traffic_area: false,
    near_entrance: false,
    estimated_foot_traffic: 250,
  },
  {
    id: 5,
    fair_id: 1,
    slot_number: 'B2',
    dimensions: '10x10 feet',
    has_power: true,
    slot_type: 'common',
    base_price: '100.00', // Issue #1078: String for type alignment
    is_available: true,
    is_corner_booth: false, // Required field from Slot interface
    corner_booth: false,
    high_traffic_area: false,
    near_entrance: false,
    estimated_foot_traffic: 300,
  },
  {
    id: 6,
    fair_id: 1,
    slot_number: 'B3',
    dimensions: '10x10 feet',
    has_power: false,
    slot_type: 'common',
    base_price: '80.00', // Issue #1078: String for type alignment
    is_available: true,
    is_corner_booth: false, // Required field from Slot interface
    corner_booth: false,
    high_traffic_area: false,
    near_entrance: false,
    estimated_foot_traffic: 280,
  },
];

export const mockBoothNeighbors: BoothNeighbor[] = [
  { slot_id: 2, slot_number: 'A2', vendor_id: 5, vendor_name: 'Artisan Crafts', distance: 110, direction: 'east' },
  { slot_id: 4, slot_number: 'B1', distance: 110, direction: 'south' },
];
